/**
 * Tenant-related utility functions
 */

const TENANT_SUBDOMAIN_KEY = 'tenantSubdomain';

/**
 * Extract subdomain from URL query params, hostname, or storage fallbacks
 * Priority:
 *   1. ?tenant= query parameter (from marketing site login)
 *   2. Hostname subdomain (tenant1.packetprospector.com) - takes priority to handle tenant switching
 *   3. sessionStorage tenantSubdomain (for app.packetprospector.com centralized login)
 *   4. localStorage tenantSubdomain (persisted from previous session)
 *
 * Examples:
 *   ?tenant=demo -> demo
 *   tenant1.localhost -> tenant1
 *   tenant1.packetprospector.com -> tenant1
 *   localhost -> null (no subdomain)
 *   packetprospector.com -> null (no subdomain)
 */
export function extractSubdomain(): string | null {
  // 1. Check for ?tenant= query parameter FIRST (highest priority)
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');
  if (tenantParam) {
    // Store in both localStorage (persists) AND sessionStorage (works in incognito)
    localStorage.setItem(TENANT_SUBDOMAIN_KEY, tenantParam);
    sessionStorage.setItem(TENANT_SUBDOMAIN_KEY, tenantParam);
    return tenantParam;
  }

  // 2. Check hostname to determine context
  const hostname = window.location.hostname;

  // Handle localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Handle Azure Static Web Apps (*.azurestaticapps.net)
  // These don't represent actual tenants, so treat as no subdomain
  if (hostname.includes('azurestaticapps.net')) {
    return null;
  }

  const parts = hostname.split('.');

  // Need at least 3 parts for a subdomain (subdomain.domain.tld)
  if (parts.length < 3) {
    return null;
  }

  // Reserved subdomains that are NOT tenant subdomains
  const reservedSubdomains = ['www', 'app', 'api', 'mail', 'smtp', 'ftp', 'admin', 'portal', 'contractor'];
  const potentialSubdomain = parts[0].toLowerCase();
  const isReservedSubdomain = reservedSubdomains.includes(potentialSubdomain);

  // 3. If NOT on a reserved subdomain, extract from hostname FIRST
  // This takes priority over cached values to handle tenant switching correctly
  if (!isReservedSubdomain) {
    // Update storage to match current URL (handles tenant switching)
    localStorage.setItem(TENANT_SUBDOMAIN_KEY, potentialSubdomain);
    sessionStorage.setItem(TENANT_SUBDOMAIN_KEY, potentialSubdomain);
    return potentialSubdomain;
  }

  // 4. On reserved subdomain (app., www., etc.) - fall back to cached tenant
  // Check sessionStorage first (works in incognito mode)
  const sessionTenant = sessionStorage.getItem(TENANT_SUBDOMAIN_KEY);
  if (sessionTenant) {
    return sessionTenant;
  }

  // 5. Check localStorage for persisted tenant
  const storedTenant = localStorage.getItem(TENANT_SUBDOMAIN_KEY);
  if (storedTenant) {
    // Copy to sessionStorage for consistency
    sessionStorage.setItem(TENANT_SUBDOMAIN_KEY, storedTenant);
    return storedTenant;
  }

  // 6. No subdomain available
  return null;
}

/**
 * Store tenant subdomain in both localStorage and sessionStorage
 * localStorage persists across sessions, sessionStorage works in incognito
 */
export function setTenantSubdomain(subdomain: string): void {
  localStorage.setItem(TENANT_SUBDOMAIN_KEY, subdomain);
  sessionStorage.setItem(TENANT_SUBDOMAIN_KEY, subdomain);
}

/**
 * Get tenant subdomain from localStorage
 */
export function getTenantSubdomain(): string | null {
  return localStorage.getItem(TENANT_SUBDOMAIN_KEY);
}

/**
 * Clear tenant subdomain from both localStorage and sessionStorage
 */
export function clearTenantSubdomain(): void {
  localStorage.removeItem(TENANT_SUBDOMAIN_KEY);
  sessionStorage.removeItem(TENANT_SUBDOMAIN_KEY);
}

/**
 * Check if running in multi-tenant mode
 */
export function isMultiTenantMode(): boolean {
  return extractSubdomain() !== null;
}

/**
 * Get tenant identifier (subdomain or default)
 */
export function getTenantIdentifier(): string {
  return extractSubdomain() || 'default';
}

/**
 * Build subdomain URL
 */
export function buildSubdomainUrl(subdomain: string, path: string = ''): string {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  // For localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const portPart = port ? `:${port}` : '';
    return `${protocol}//${subdomain}.localhost${portPart}${path}`;
  }

  // For production (remove existing subdomain if present)
  const parts = hostname.split('.');
  const domain = parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
  const portPart = port ? `:${port}` : '';

  return `${protocol}//${subdomain}.${domain}${portPart}${path}`;
}

/**
 * Validate subdomain format
 */
export function isValidSubdomain(subdomain: string): boolean {
  // Only lowercase letters, numbers, and hyphens
  // Must start with a letter or number
  // Must end with a letter or number
  // Length between 3 and 63 characters
  const regex = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
  return regex.test(subdomain);
}
